import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    Card,
    CardContent,
    StatusBadge
} from "@solomon-labs/design/components/ui";
import React from "react";

import { FormErrorRetry } from "@/pages/shared/FormErrorRetry";

import type { FindSlice, SourceSlice } from "../steps";

import { AddressField } from "./AddressField";
import { ConnectedWalletHint } from "./ConnectedWalletHint";
import { SourceDetectionSummary } from "./SourceDetectionSummary";
import { StepFieldGroup } from "./StepFieldGroup";

/* ── Step 0 — Add Address (combined) ────────────────────────────
 *
 * The single step that used to be Find Position + Verify Ownership. The user
 * pastes the wallet they want to monitor. Discovery runs as the address resolves;
 * the verify action (`VerifyActionBar`) proves ownership. This body
 * owns the address field, its classification/validation lines, the
 * connected-wallet hint that explains the verify button's form, a read-only
 * Address balance preview and backend-discovered holdings. Self-collapses to
 * the read-only source field as the resting summary on later steps.
 */
export function VerifyWalletGroup({
    collapsed,
    ownership = null,
    source,
    find
}: {
    collapsed: boolean;
    /** Ownership status; read-only holdings are available before verification. */
    verified: boolean;
    /** Field badge after the verify step: `verified` (wallet proof) shows
     *  Verified, `pending` (direct verification) shows Pending Verification;
     *  otherwise a matching connected wallet shows Connected. */
    ownership?: "verified" | "pending" | null;
    source: SourceSlice;
    find: FindSlice;
}): React.ReactElement {
    const { kind, connectedWalletAddress, multisigSummary, address } = source;

    const showInvalid =
        source.addressTouched &&
        source.address.length > 0 &&
        source.addressInvalid;

    // Connected-wallet feedback: explains why the verify button reads
    // "Verify Ownership" vs "Switch Wallet".
    //
    // - EOA, the source wallet is connected: no line; the field takes the DS
    //   success rail and a "Connected" StatusBadge sits inside it.
    // - EOA mismatch, or a Squads source whose connected wallet is not an
    //   authorized member: a `small` error hint naming the connected wallet
    //   (DS `Address`) and what to switch to.
    // - Squads, an authorized member is connected: a muted membership line.
    //   The field holds the multisig, not the connected wallet, so it does not
    //   take the "Connected" state.
    const hasConnectedWallet =
        connectedWalletAddress !== null && connectedWalletAddress.length > 0;
    const eoaSourceWithConnectedWallet =
        kind === "eoa" && hasConnectedWallet && address.trim().length > 0;
    const eoaSourceMatch =
        eoaSourceWithConnectedWallet &&
        address.trim() === connectedWalletAddress;
    const eoaSourceMismatch = eoaSourceWithConnectedWallet && !eoaSourceMatch;
    const multisigSignerMismatch =
        kind === "multisig-squads" &&
        hasConnectedWallet &&
        (multisigSummary?.signerStatus === "not-member" ||
            multisigSummary?.signerStatus === "cannot-verify");
    const multisigSignerMatch =
        kind === "multisig-squads" &&
        hasConnectedWallet &&
        multisigSummary?.signerStatus === "can-verify";
    // An already-registered source is a dead end whoever is connected, so it
    // takes no switch hint or membership line: the field's error state and
    // the "Already Registered" badge say everything.
    const alreadyRegistered = source.alreadyRegistered;
    const preview = find.foundPosition ?? find.addressPreview;
    const switchHint = alreadyRegistered
        ? null
        : eoaSourceMismatch
          ? "Switch to the Position Source address to verify ownership."
          : multisigSignerMismatch
            ? multisigSummary?.signerStatus === "cannot-verify"
                ? "This member can't verify (needs Vote or Execute). Switch to an authorized member wallet."
                : null
            : null;
    // A connected wallet that is not a member of the multisig: a red "Not a
    // Signer" badge, then "[wallet] connected. Switch to a valid signer."
    const multisigNotMember =
        !alreadyRegistered &&
        multisigSignerMismatch &&
        multisigSummary?.signerStatus !== "cannot-verify";
    const fieldUsable = !collapsed && !showInvalid && !alreadyRegistered;
    const showPending = fieldUsable && ownership === "pending";
    const showVerified = fieldUsable && ownership === "verified";
    const showConnected = fieldUsable && ownership === null && eoaSourceMatch;

    // Once collapsed (the resting summary on later steps), a Squads source
    // resolves to its vault address — where the position's funds live. The
    // expanded, editable field keeps the raw entered source.
    const displaySourceAddress =
        collapsed &&
        source.kind === "multisig-squads" &&
        source.multisigSummary?.vaultAddress
            ? source.multisigSummary.vaultAddress
            : source.address;

    return (
        <StepFieldGroup collapsed={collapsed}>
            <AddressField
                label={source.addressLabel}
                value={displaySourceAddress}
                onChange={source.onAddressChange}
                onBlur={source.onAddressBlur}
                readOnly={collapsed}
                error={!collapsed && (showInvalid || alreadyRegistered)}
                // The step's content box is the measure: the field fills it.
                fill
                placeholder={source.addressPlaceholder}
                // Pending direct verification drops the success rail: nothing is
                // proven yet.
                success={showConnected || showVerified}
                endAddon={
                    showPending ? (
                        <StatusBadge tone="warning" variant="filled">
                            Pending Verification
                        </StatusBadge>
                    ) : showVerified ? (
                        <StatusBadge tone="info" variant="filled">
                            Verified
                        </StatusBadge>
                    ) : showConnected ? (
                        <StatusBadge tone="success" variant="filled">
                            Connected
                        </StatusBadge>
                    ) : undefined
                }
            />
            {!collapsed && showInvalid && (
                <Text variant="label" className="mt-1.5 text-error-base">
                    {source.addressError}
                </Text>
            )}
            {!collapsed && source.showRetry && (
                <FormErrorRetry
                    className="mt-1.5"
                    error="Address check failed."
                    onRetry={source.onRetry}
                />
            )}
            {!collapsed &&
                (source.notice !== null ? (
                    <Text
                        variant="label"
                        className="mt-1.5 text-foreground-muted"
                    >
                        {source.notice}
                    </Text>
                ) : (
                    <SourceDetectionSummary
                        sourceKind={source.kind}
                        connectedWalletAddress={source.connectedWalletAddress}
                        multisigSummary={source.multisigSummary}
                        enteredAddress={source.address}
                        alreadyRegistered={alreadyRegistered}
                    />
                ))}
            {!collapsed && switchHint !== null && connectedWalletAddress && (
                <ConnectedWalletHint
                    wallet={connectedWalletAddress}
                    message={switchHint}
                    tone="error"
                />
            )}
            {!collapsed && multisigNotMember && connectedWalletAddress && (
                <ConnectedWalletHint
                    wallet={connectedWalletAddress}
                    message="Switch to a valid signer."
                    tone="muted"
                    leading={
                        <StatusBadge tone="error" variant="filled">
                            Not a Signer
                        </StatusBadge>
                    }
                />
            )}
            {!collapsed &&
                !alreadyRegistered &&
                multisigSignerMatch &&
                connectedWalletAddress && (
                    <ConnectedWalletHint
                        wallet={connectedWalletAddress}
                        tone="muted"
                        leading={
                            <StatusBadge tone="success" variant="filled">
                                Valid Signer
                            </StatusBadge>
                        }
                    />
                )}
            {!collapsed && find.message !== null && (
                <Text
                    variant="small"
                    role={find.messageTone === "error" ? "alert" : undefined}
                    className={`mt-3 ${
                        find.messageTone === "error"
                            ? "text-error-base"
                            : find.messageTone === "success"
                              ? "text-success-base"
                              : "text-foreground-muted"
                    }`}
                >
                    {find.message}
                </Text>
            )}
            {/* Address uses the live RPC balance; backend LP observations join
                the preview as automatic discovery settles. Empty wallets
                remain registerable and render the same table at $0.00. */}
            {!collapsed && !alreadyRegistered && preview !== null && (
                <Card
                    surface="sunken"
                    border="line"
                    // Same measure as the address field above it (the
                    // content box), so their right edges align.
                    className="mt-4 w-full"
                >
                    <CardContent className="p-3">
                        {/* Positions takes the free width, Balance sits right.
                            Cells butt (no column gap) so the rule under the
                            header row is one unbroken line across the card. */}
                        <div className="grid w-full grid-cols-[1fr_auto] items-baseline">
                            <Text
                                variant="fine-print"
                                render={<span />}
                                className="border-b border-line-base pr-6 pb-2 text-foreground-imprint"
                            >
                                Positions
                            </Text>
                            <Text
                                variant="fine-print"
                                render={<span />}
                                className="border-b border-line-base pb-2 text-right text-foreground-imprint"
                            >
                                Balance
                            </Text>
                            {(
                                preview.rows ?? [
                                    {
                                        key: "address",
                                        label: "USDv",
                                        surface: "Address",
                                        positionCount: 1,
                                        balanceLabel: preview.balanceLabel
                                    }
                                ]
                            ).map((row) => (
                                <React.Fragment key={row.key}>
                                    <Text
                                        variant="body"
                                        render={<span />}
                                        className="pt-2 pr-6 text-foreground-base"
                                    >
                                        {row.label}
                                        <Text
                                            variant="fine-print"
                                            render={<span />}
                                            className="block text-foreground-imprint"
                                        >
                                            {row.surface}
                                            {row.surface !== "Address"
                                                ? ` (${row.positionCount} LP ${row.positionCount === 1 ? "position" : "positions"})`
                                                : ""}
                                        </Text>
                                    </Text>
                                    <Text
                                        variant="body"
                                        render={<span />}
                                        className="pt-2 text-right text-foreground-base"
                                    >
                                        {row.balanceLabel}
                                    </Text>
                                </React.Fragment>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </StepFieldGroup>
    );
}
